import React, { useEffect, useRef } from 'react'
import { StyleSheet, View } from 'react-native'
import PropTypes from 'prop-types'

import Spinner from '../Spinner'
import Icon from '../Icons'
import { useTheme } from '../../theming/ThemeProvider'
import { SelectInputStyledComponent, MultiselectContainer } from './styles'
import SelectableInput from '../SelectableInput'
import { Popover, Position } from 'evergreen-ui'

export default function MultiselectInput (props) {
  const {
    name, label, selected, keyProp, nameProp, getOptionLabel, onChange, icon,
    options, placeholder, loading, filterAsync, error, readonly, loadMore, noMoreOptions, globalLoading
  } = props

  const selectRef = useRef(null)
  const { theme } = useTheme()
  const styles = useStyles(theme)

  useEffect(() => {
    const select = selectRef.current
    if (select) {
      select.setState({
        ...select.state,
        isLoading: globalLoading,
        loadedOptions: options,
        passEmptyOptions: globalLoading
      })
    }
  }, [globalLoading, options])

  const customStyles = {
    container: (provided) => ({
      ...provided,
      fontFamily: 'roboto-regular',
      fontSize: 12,
      background: theme[readonly ? 'DISABLED_INPUT_COLOR' : 'WHITE'],
      borderRadius: 5
    }),
    singleValue: (provided) => ({
      ...provided,
      color: theme.BUNKER
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: theme.WHITE,
      position: 'relative',
      boxShadow: 'none'
    }),
    option: (provided, state) => ({
      ...provided,
      color: theme[state.isFocused ? 'BUNKER_TEXT' : state.isSelected ? 'WHITE' : 'BUNKER'],
      backgroundColor: theme[state.isFocused ? 'GALLERY' : state.isSelected ? 'BUNKER' : 'WHITE']
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: 5
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      display: 'none'
    })
  }

  return (
    <Popover
      position={Position.BOTTOM_LEFT}
      statelessProps={{ transform: 'translateY(-75px)!important' }}
      onClose={() => filterAsync && filterAsync('')}
      content={(
        <View style={styles.menu}>
          <SelectInputStyledComponent
            ref={selectRef}
            className='react-select'
            classNamePrefix='react-select'
            loadingMessage={() => <Spinner />}
            styles={customStyles}
            value={selected}
            isLoading={globalLoading}
            placeholder={placeholder}
            onChange={(newSelected) => onChange(name, newSelected)}
            defaultOptions={filterAsync ? options : null}
            options={!filterAsync ? options : null}
            isMulti
            isSearchable
            getOptionLabel={option => getOptionLabel ? getOptionLabel(option) : option[nameProp]}
            getOptionValue={option => option[keyProp]}
            closeMenuOnSelect={false}
            loadOptions={filterAsync}
            isClearable
            menuIsOpen
            autoFocus
            handleLoadMore={!noMoreOptions ? loadMore : () => null}
            loading={loading}
          />
        </View>
      )}
    >
      {({ toggle, getRef }) => (
        <MultiselectContainer ref={(ref) => getRef(ref)}>
          <SelectableInput
            label={label}
            value={selected && selected.length ? 'Multiple options selected' : ''}
            icon={icon}
            rightIcon={
              <Icon
                name='FilterArrow'
                height={10}
                width={10}
                fill={theme.SLATE_GRAY}
              />
            }
            placeholder={placeholder}
            onPress={toggle}
            error={error}
          />
        </MultiselectContainer>
      )}
    </Popover>
  )
}

const useStyles = (theme) => StyleSheet.create({
  menu: {
    width: 300,
    padding: 10,
    backgroundColor: theme.CARD_BACKGROUND
  }
})

MultiselectInput.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  selected: PropTypes.any,
  keyProp: PropTypes.string,
  nameProp: PropTypes.string,
  placeholder: PropTypes.string,
  icon: PropTypes.string,
  options: PropTypes.array,
  onChange: PropTypes.func,
  loading: PropTypes.bool,
  globalLoading: PropTypes.bool,
  filterAsync: PropTypes.func,
  getOptionLabel: PropTypes.func,
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool
  ]),
  loadMore: PropTypes.func,
  readonly: PropTypes.bool,
  noMoreOptions: PropTypes.bool
}

MultiselectInput.defaultProps = {
  name: '',
  label: '',
  selected: null,
  keyProp: '',
  nameProp: '',
  placeholder: '',
  icon: '',
  options: [],
  onChange: () => {},
  loading: false,
  filterAsync: null,
  getOptionLabel: null,
  error: false,
  loadMore: null,
  globalLoading: false,
  noMoreOptions: true
}
